import { useState, useEffect } from 'react';
import {
    Drawer,
    Select,
    Button,
    Skeleton,
    Empty,
    Collapse,
    Space,
    Popconfirm,
    Spin,
    Card,
    notification
} from 'antd';
import {
    PlusOutlined,
    NumberOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import ImageWithSpinner from '../content/imagewithspinner';
import SortableItem from "../common/sortableitem";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    arrayMove
} from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';
import { addMenuItemAddOnGroup, getAllMenuAddOnGroups, getMenuAddOnsForMenu, removeMenuItemAddOnGroup, updateMenuAddOnGroupSortOrder } from '../../services/menuaddonservice';
import { useAuth } from "../../auth/authcontext";

const { Option } = Select;
const { Panel } = Collapse;

interface GroupOption {
    guid: string;
    name: string;
}

type MenuAddOn = {
    menuAddOnGroupGuid: string;
    menuAddOnGroupName: string;
    menuAddOnSortOrder: number;
    menuMenuAddOnGroupSortOrder: number;
    menuAddOnName: string;
    imageUrl: string;
};

type GroupedMenuAddOns = {
    [groupName: string]: MenuAddOn[];
};

type SortableGroup = {
    id: string;
    content: string;
};

interface Props {
    drawerVisible: boolean;
    closeDrawer: () => void;
    menuItemGuid: string | null;
    isDisabled: boolean;
}

const DrawerAddOns: React.FC<Props> = ({
    drawerVisible,
    closeDrawer,
    menuItemGuid,
    isDisabled
}) => {

    const [menuAddOns, setMenuAddOns] = useState<MenuAddOn[]>([]);
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [availableGroups, setAvailableGroups] = useState<GroupOption[]>([]);
    const [newGroup, setNewGroup] = useState<string | null>(null);
    const [addingGroup, setAddingGroup] = useState<boolean>(false);
    const [existingGroupGuids, setExistingGroupGuids] = useState<Set<string>>(new Set());
    const [deletingGroup, setDeletingGroup] = useState<boolean>(false);
    const [deletingGroupName, setDeletingGroupName] = useState<string | null>(null);

    const [isSortOrderView, setSortOrderView] = useState<boolean>(false);
    const [sortableItems, setSortableItems] = useState<SortableGroup[]>([]);
    const { userId: userGuid } = useAuth();
    
    const closeMenuOptionsDrawer = () => {
        setNewGroup(null);
        setSortableItems([]);
        setSortOrderView(false);
        closeDrawer();
    };

    useEffect(() => {
        setIsFetching(true);
        if (menuItemGuid && menuItemGuid !== "(SELECT)") {
            const fetchAddOns = async () => {
                try {
                    
                    const data = await getMenuAddOnsForMenu(menuItemGuid);
                    setMenuAddOns(data);                    

                    setIsFetching(false);
                } catch (error) {
                    console.error('Error fetching menu options:', error);
                    setIsFetching(false);
                }
            };
            fetchAddOns();
        } else {
            setMenuAddOns([]);
            setIsFetching(false);
        }
    }, [menuItemGuid]);

    useEffect(() => {
        const fetchAvailableGroups = async () => {
            try {                
                const data = await getAllMenuAddOnGroups();
                setAvailableGroups(data);                
            } catch (error) {
                console.error("Error fetching available groups:", error);
            }
        };
        fetchAvailableGroups();
    }, [menuItemGuid]);

    useEffect(() => {
        const guids = new Set(menuAddOns.map(option => option.menuAddOnGroupGuid));
        setExistingGroupGuids(guids);
    }, [menuAddOns]);

    const groupedMenuAddOns: GroupedMenuAddOns = menuAddOns.reduce((acc, option) => {
        const groupName = option.menuAddOnGroupName;
        if (!acc[groupName]) {
            acc[groupName] = [];
        }
        acc[groupName].push(option);
        return acc;
    }, {} as GroupedMenuAddOns);

    const handleAddAddOnGroup = async () => {
        setAddingGroup(true);
        try {

            const selectedGroup = availableGroups.find((group) => group.guid === newGroup);
            const selectedMenuItemGuid = menuItemGuid;

            if (selectedGroup && selectedMenuItemGuid) {
                if (existingGroupGuids.has(selectedGroup.guid)) {
                    notification.warning({
                        message: 'Warning',
                        description: `The menu add on group "${selectedGroup.name}" is already added.`,
                        placement: 'bottomRight',
                        duration: 3,
                    });
                    return;
                }

                // Prepare the data to send to the API
                const payload = {
                    menuItemId: selectedMenuItemGuid,
                    menuAddOnGroupGuid: selectedGroup.guid,
                    createdByGuid: userGuid ?? ''
                };

                const response = await addMenuItemAddOnGroup(payload);
                if (response.status === 200) {
                    // Refetch the menu add ons for the current menu item to get the updated list from the server.
                    const fetchMenuAddOns = async () => {
                        setIsFetching(true);
                        try {
                            const data = await getMenuAddOnsForMenu(selectedMenuItemGuid);
                            setMenuAddOns(data);
                            notification.open({
                                message: "Menu Add On Group Added",
                                description: `The menu add on group "${selectedGroup.name}" has been associated with this menu item successfully.`,
                                type: "success",
                                placement: 'bottomRight',
                                showProgress: true,
                                pauseOnHover: true,
                            });
                        } catch (error) {
                            console.error('Error refetching menu add ons:', error);
                            notification.error({
                                message: "Error Refetching Menu Add Ons",
                                description: "Failed to update the list of menu add ons after adding the group.",
                            });
                        } finally {
                            setIsFetching(false);
                        }
                    };
                    fetchMenuAddOns();
                } else {
                    notification.error({
                        message: "Error Adding Menu Add On Group",
                        description: `Failed to associate the group "${selectedGroup.name}" with this menu item.`,
                        placement: 'bottomRight',
                        showProgress: true,
                        pauseOnHover: true,
                    });
                }
            }
        } catch (error) {
            console.error("Error adding menu add on group:", error);
            notification.open({
                message: "Error Adding Menu Add On Group",
                description: "An error occurred while adding the menu add on group.",
                type: "error",
            });
        } finally {
            setAddingGroup(false);
            setNewGroup(null);
        }
    };

    const handleRemoveAddOnGroup = async (groupGuidToRemove: string, groupDisplayName: string) => {
        setDeletingGroup(true);
        setDeletingGroupName(groupDisplayName);
        try {
            const selectedMenuItemGuid = menuItemGuid;

            if (selectedMenuItemGuid && groupGuidToRemove) {
                const payload = {
                    menuItemId: selectedMenuItemGuid,
                    menuAddOnGroupGuid: groupGuidToRemove,
                };

                const response = await removeMenuItemAddOnGroup(payload);

                if (response.status === 200) {
                    // Refetch menu options to update the UI
                    const fetchMenuAddOns = async () => {
                        setIsFetching(true);
                        try {
                            const data = await getMenuAddOnsForMenu(selectedMenuItemGuid);
                            setMenuAddOns(data);
                             notification.open({
                                message: 'Menu Add On Group Removed',
                                description: `The menu add on group "${groupDisplayName}" has been removed successfully.`,
                                type: 'success',
                                placement: 'bottomRight',
                                showProgress: true,
                                pauseOnHover: true,
                            });
                        } catch (error) {
                            console.error('Error refetching menu add ons:', error);
                            notification.error({
                                message: 'Error Refetching Menu Add Ons',
                                description: 'Failed to update the list of menu add ons after removing the group.',
                                type: 'error',
                            });
                        } finally {
                            setIsFetching(false);
                            setDeletingGroup(false);
                            setDeletingGroupName(null);
                        }
                    };
                    fetchMenuAddOns();
                } else {
                    notification.error({
                        message: 'Error Removing Menu Add On Group',
                        description: `Failed to remove the group "${groupDisplayName}".`,
                        type: 'error',
                    });
                }
            }
        } catch (error) {
            console.error('Error deleting menu add on group:', error);
            notification.open({
                message: 'Error Deleting Menu add on Group',
                description: 'An error occurred while deleting the menu add on group.',
                type: 'error',
                placement: 'bottomRight',
                showProgress: true,
                pauseOnHover: true,
            });
        } finally {
            setDeletingGroup(false);
            setDeletingGroupName(null);
        }
    };


    ///// sorting code start
    const initSortableItems = () => {
        setSortOrderView(true);
        const sortableData = getGroupDetailsForSorting(groupedMenuAddOns);
        setSortableItems(sortableData);
    }

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setSortableItems((sortableItems) => {
                const oldIndex = sortableItems.findIndex((item) => item.id === active.id);
                const newIndex = sortableItems.findIndex((item) => item.id === over?.id);
                const newOrderedItems = arrayMove(sortableItems, oldIndex, newIndex);
                return newOrderedItems;
            });
        }
    };

    const handleSaveOrder = () => {
        UpdateAddOnsSortOrder();
    };

    function getGroupDetailsForSorting(
        data: GroupedMenuAddOns
    ): SortableGroup[] {
        const result: SortableGroup[] = [];

        for (const groupName in data) {
            if (Object.hasOwn(data, groupName)) {
                const options = data[groupName];

                if (options && options.length > 0) {
                    const firstOption = options[0];

                    if (drawerVisible) { //  addon group
                        result.push({
                            id: firstOption.menuAddOnGroupGuid,
                            content: firstOption.menuAddOnGroupName
                        });
                    }
                }
            }
        }

        return result;
    }

    const sortOrderViewContent = (
        <div style={{ padding: '5px' }}>
            <div style={{
                backgroundColor: '#eff0f0',
                border: '1px solidrgb(161, 165, 170)',
                padding: '16px 20px',
                borderRadius: '10px',
                maxWidth: '450px',
                margin: '20px auto',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                fontFamily: 'Arial, sans-serif',
                color: '#343a40',
                lineHeight: '1.6',
            }}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={sortableItems.map(item => item.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {sortableItems.map((item) => (
                            <SortableItem key={item.id} id={item.id} content={item.content} />
                        ))}
                    </SortableContext>
                </DndContext>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <Space>
                    <Button
                        onClick={() => { setSortOrderView(false) }}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="blue"
                        variant="filled"
                        onClick={handleSaveOrder}
                    >
                        Save
                    </Button>
                </Space>
            </div>
        </div>
    );

    const UpdateAddOnsSortOrder = async () => {
        setIsFetching(true);
        try {
            const selectedMenuItemGuid = menuItemGuid;
            const sortedMenuAddOnGroupGuids = sortableItems.map(item => item.id);

            // Prepare the data to send to the API
            const payload = {
                menuItemId: selectedMenuItemGuid,
                menuAddOnGroupGuids: sortedMenuAddOnGroupGuids,
            };
            const response = await updateMenuAddOnGroupSortOrder(payload);
            if (response.status === 200) {
                // Refetch the menu add ons for the current menu item to get the updated list from the server.  
                try {
                    const data = await getMenuAddOnsForMenu(selectedMenuItemGuid);
                    setMenuAddOns(data);
                    notification.open({
                        message: "Success",
                        description: `Menu AddOn group SortOrder updated successfully.`,
                        type: "success",
                        placement: 'bottomRight',
                        showProgress: true,
                        pauseOnHover: true,
                    });
                } catch (error) {
                    console.error('Error refetching menu options:', error);
                    notification.error({
                        message: "Error",
                        description: "Failed to update the list of menu addons after updating sortorder.",
                    });
                }
            } else {
                notification.error({
                    message: "Error",
                    description: `Failed to update menu addon group sortorder.`,
                    placement: 'bottomRight',
                    showProgress: true,
                    pauseOnHover: true,
                });
            }
        } catch (error) {
            console.error("An error occurred while updating sort order for addon groups.:", error);
            notification.open({
                message: "Error",
                description: "An error occurred while updating sort order for addon groups.",
                type: "error",
            });
        } finally {
            setSortableItems([]);
            setSortOrderView(false);
            setIsFetching(false);
        }
    }
    /// sorting code end
    let drawerContent: React.ReactNode;

    if (isFetching) {
        drawerContent = <Skeleton style={{ padding: '20px' }} active />;
    } else if (menuAddOns.length === 0) {
        drawerContent = <Empty style={{ padding: '20px' }} description="No add ons available" />;
    } else if (isSortOrderView) {
        drawerContent = sortOrderViewContent;
    } else {
        drawerContent = (
            <Collapse
                accordion
                style={{
                    border: 'none',
                    backgroundColor: '#f8f8f8',
                    margin: '20px 0',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.20)'
                }}
                expandIconPosition="left"
            >
                {Object.entries(groupedMenuAddOns).map(([groupName, options]) => {
                    const groupGuid = options[0]?.menuAddOnGroupGuid;
                    return (
                        <Panel
                            header={
                                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                                    <span>{groupName}</span>
                                    {deletingGroup && deletingGroupName === groupName ? (
                                        <Spin size="small" />
                                    ) : (
                                        <Popconfirm
                                            title="Are you sure you want to remove this menu add on group?"
                                            onConfirm={() => handleRemoveAddOnGroup(groupGuid, groupName)}
                                            okText="Yes"
                                            cancelText="No"
                                        >
                                            <Button size="small" danger icon={<DeleteOutlined />} disabled={isDisabled} />
                                        </Popconfirm>
                                    )}
                                </Space>
                            }
                            key={groupName}
                            style={{
                                borderBottom: '1px solid #e8e8e8',
                                marginBottom: '0px',
                            }}
                        >
                            {options.map((option) => (
                                <Card
                                    key={option.menuAddOnName}
                                    size="small"
                                    style={{
                                        marginBottom: '8px',
                                        fontSize: '12px',
                                        borderWidth: '1px',
                                        borderStyle: 'solid',
                                        borderRadius: '4px',
                                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Space>
                                        <div style={{ marginRight: '10px', width: '50px', height: '50px' }}>
                                            {option.imageUrl && <ImageWithSpinner imageUrl={option.imageUrl} />}
                                        </div>
                                        <div>{option.menuAddOnName}</div>
                                    </Space>
                                </Card>
                            ))}
                        </Panel>
                    );
                })}
            </Collapse>
        );
    }

    return (
        <Drawer
            title="Menu AddOns"
            placement="right"
            closable
            onClose={closeMenuOptionsDrawer}
            open={drawerVisible}
            width={500}
            styles={{
                header: {
                    color: 'white',
                    backgroundColor: 'var(--color-primary)',
                    fontWeight: 350,
                    fontSize: '14px',
                    border: 0,
                    minHeight: '40px'
                }
            }}
        >
            <Space>
                <Select
                    placeholder="Select Group"
                    style={{ width: 200 }}
                    onChange={(value) => setNewGroup(value)}
                    value={newGroup ?? undefined}
                    disabled={isDisabled}
                >
                    {availableGroups.map((group) => (
                        <Option key={group.guid} value={group.guid}>
                            {group.name}
                        </Option>
                    ))}
                </Select>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddAddOnGroup}
                    loading={addingGroup}
                    disabled={!newGroup}
                >
                    Add Group
                </Button>
            </Space>

            <Space style={{ marginTop: '8px' }}>
                <Button
                    type="default"
                    icon={<NumberOutlined />}
                    onClick={initSortableItems}
                    disabled={isDisabled}
                >
                    Reorder Groups
                </Button>
            </Space>

            {drawerContent}
        </Drawer>
    );
};

export default DrawerAddOns;